import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Button, Tag, Modal, Form, Input, Upload, Select, message, Spin,
} from 'antd';
import {
  ExclamationCircleOutlined, PictureOutlined, LoadingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router';
import api from '../../../../../core/axios/axiosInstance';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProfile } from '../../../../../core/data/redux/slices/ProfileSlice';

const { confirm } = Modal;

const META_APP_ID    = "878581404547460";
const META_CONFIG_ID = "3324225224394467";
const META_SDK_VER   = "v24.0";

function loadFacebookSdk() {
  if (document.getElementById("facebook-jssdk")) return;
  window.fbAsyncInit = () => {
    window.FB.init({ appId: META_APP_ID, autoLogAppEvents: true, xfbml: true, version: META_SDK_VER });
  };
  const s   = document.createElement("script");
  s.id      = "facebook-jssdk";
  s.src     = "https://connect.facebook.net/en_US/sdk.js";
  s.async   = true;
  s.defer   = true;
  document.body.appendChild(s);
}

const WabaSetting = () => {
  const userProfile = useSelector((state) => state.profile);
  const dispatch    = useDispatch();
  const navigate    = useNavigate();

  const [wabaConfigured, setWabaConfigured] = useState(
    userProfile?.accounts?.whatsapp?.isConnected || false
  );
  const [profileData,  setProfileData]  = useState({});
  const [loading,      setLoading]      = useState(false);
  const [connecting,   setConnecting]   = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [imageUrl,     setImageUrl]     = useState(null);

  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [regenerating,   setRegenerating]   = useState(false);
  const [tokenForm] = Form.useForm();

  const [savingProfile, setSavingProfile] = useState(false);

  const [configModalOpen,     setConfigModalOpen]     = useState(false);
  const [forceConfirmVisible, setForceConfirmVisible] = useState(false);
  const [forceConfirmEmail,   setForceConfirmEmail]   = useState("");
  const [pendingPayload,      setPendingPayload]      = useState(null);
  const [configForm] = Form.useForm();

  const [form] = Form.useForm();

  useEffect(() => { loadFacebookSdk(); }, []);

  const getQualityColor = (score) =>
    ({ GREEN: "green", YELLOW: "orange", RED: "red" })[score] || "default";

  const beforeUpload = (file) => {
    const ok = file.type === "image/jpeg" || file.type === "image/png";
    if (!ok) message.error("Only JPG/PNG files are allowed");
    return ok;
  };

  const handleImageChange = (info) => {
    const file = info.file.originFileObj;
    setProfileImage(file);
    const reader = new FileReader();
    reader.onload = () => setImageUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const fetchWabaProfile = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("api/whatsapp/profile");
      const d = data.data;
      console.log("[WabaSetting] fetchWabaProfile raw response:", JSON.stringify(d, null, 2));
      console.log("[WabaSetting] displayName:", d?.displayName, "| profile.displayName:", d?.profile?.displayName, "| name_status:", d?.nameStatus);
      console.log("[WabaSetting] qualityRating:", JSON.stringify(d?.qualityRating), "| messagingLimit:", d?.messagingLimit);
      console.log("[WabaSetting] accountReviewStatus:", d?.accountReviewStatus, "| status:", d?.status, "| wabaId:", d?.wabaId);
      setProfileData(d);
      tokenForm.setFieldsValue({ accessToken: d?.accessToken });
      form.setFieldsValue({
        address:     d?.profile?.address     || "",
        email:       d?.profile?.email       || "",
        description: d?.profile?.description || "",
        vertical:    d?.profile?.vertical,
        websites:    d?.profile?.websites?.[0] || [],
        displayName: d?.profile?.displayName  || "",
      });
      setImageUrl(d?.profile?.profilePictureUrl);
    } catch (e) {
      console.error("[WabaSetting] fetchWabaProfile error:", e?.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  }, [form, tokenForm]);

  useEffect(() => {
    if (!wabaConfigured) return;
    fetchWabaProfile();
  }, [wabaConfigured, fetchWabaProfile]);

  useEffect(() => {
    if (!wabaConfigured) return;
    api.post("api/whatsapp/resubscribe-webhook").catch(() => {});
  }, [wabaConfigured]);

  /* ── Embedded Signup ─────────────────────────────────────────────────────── */
  // sessionInfoVersion:3 — Meta sends waba_id + phone_number_id via postMessage
  // (NOT in signedRequest). We capture it before calling FB.login, then send
  // both the auth code and the session IDs to the backend.
  const handleEmbeddedSignup = () => {
    if (!window.FB) {
      message.error("Facebook SDK not loaded yet — please wait a moment and try again.");
      return;
    }
    setConnecting(true);

    // Capture WABA/phone IDs from Meta's postMessage (sessionInfoVersion: 3)
    let sessionWabaId = null;
    let sessionPhoneNumberId = null;

    const onMessage = (event) => {
      if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") return;
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type === "WA_EMBEDDED_SIGNUP") {
          if (data.event === "FINISH") {
            sessionWabaId       = data.data?.waba_id       || null;
            sessionPhoneNumberId = data.data?.phone_number_id || null;
            console.log("[EmbeddedSignup] postMessage FINISH — wabaId:", sessionWabaId, "phoneNumberId:", sessionPhoneNumberId);
          } else {
            console.log("[EmbeddedSignup] postMessage event:", data.event, data);
          }
        }
      } catch (_) {}
    };
    window.addEventListener("message", onMessage);

    window.FB.login(
      (response) => {
        window.removeEventListener("message", onMessage);
        const code = response?.authResponse?.code;
        if (!code) {
          setConnecting(false);
          return;
        }

        api.post("api/whatsapp/embedded-signup/exchange", {
          code,
          wabaId:        sessionWabaId,
          phoneNumberId: sessionPhoneNumberId,
        })
          .then(() => {
            message.success("WhatsApp connected successfully via Meta!");
            dispatch(fetchProfile());
            setWabaConfigured(true);
          })
          .catch((err) => {
            if (err.response?.status === 409) {
              Modal.warning({
                title:   "WABA Already Connected",
                content: `This WhatsApp Business Account is already connected to ${err.response?.data?.connectedEmail || "another account"}. Please contact support if you believe this is an error.`,
              });
            } else {
              message.error(err.response?.data?.message || "Failed to connect WhatsApp. Please try again.");
            }
          })
          .finally(() => {
            setConnecting(false);
          });
      },
      {
        config_id:                      META_CONFIG_ID,
        response_type:                  "code",
        override_default_response_type: true,
        extras:                         { sessionInfoVersion: 3 },
      }
    );
  };

  /* ── Manual connect (fallback) ───────────────────────────────────────────── */
  const handleSaveConfig = async (forceDisconnect = false) => {
    try {
      setConnecting(true);
      const values  = await configForm.validateFields();
      const payload = { ...values, ...(forceDisconnect && { forceDisconnect: true }) };
      await api.post("api/whatsapp/connect", payload);
      message.success("WhatsApp API configured successfully");
      dispatch(fetchProfile());
      setConfigModalOpen(false);
      setForceConfirmVisible(false);
      setPendingPayload(null);
      configForm.resetFields();
      setWabaConfigured(true);
    } catch (error) {
      if (error.response?.status === 409) {
        const values = await configForm.validateFields();
        setPendingPayload(values);
        setForceConfirmEmail(error.response.data?.connectedEmail || "another account");
        setConfigModalOpen(false);
        setForceConfirmVisible(true);
      } else {
        message.error(error.response?.data?.message || "Failed to configure WhatsApp");
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleForceConnect = async () => {
    try {
      setConnecting(true);
      await api.post("api/whatsapp/connect", { ...pendingPayload, forceDisconnect: true });
      message.success("WhatsApp API configured successfully");
      dispatch(fetchProfile());
      setForceConfirmVisible(false);
      setPendingPayload(null);
      configForm.resetFields();
      setWabaConfigured(true);
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to configure WhatsApp");
    } finally {
      setConnecting(false);
    }
  };

  const handleRegenerateToken = async () => {
    try {
      setRegenerating(true);
      const { data } = await api.post("api/whatsapp/refresh-token", {
        access_token_waba: tokenForm.getFieldValue("accessToken"),
      });
      tokenForm.setFieldsValue({ accessToken: data.accessToken });
      message.success("Access token updated successfully");
      window.location.reload();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update access token");
    } finally {
      setRegenerating(false);
    }
  };

  const handleDeleteIntegration = () => {
    confirm({
      title: "Delete WhatsApp integration?",
      icon: <ExclamationCircleOutlined />,
      content: "This will completely delete your WhatsApp integration from the CRM.",
      okText: "Yes, delete",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        try {
          await api.post("api/whatsapp/disconnect");
          message.success("WhatsApp integration disconnected");
          dispatch(fetchProfile());
          setWabaConfigured(false);
          setProfileData({});
          form.resetFields();
          tokenForm.resetFields();
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to disconnect WhatsApp");
        }
      },
    });
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const values   = await form.validateFields();
      const formData = new FormData();
      if (profileImage) formData.append("profile", profileImage);
      formData.append("displayName",  values.displayName);
      formData.append("address",      values.address);
      formData.append("email",        values.email);
      formData.append("description",  values.description);
      formData.append("vertical",     values.vertical);
      if (values.websites) formData.append("websites", values.websites);
      await api.put("api/whatsapp/profile/edit", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      message.success("WhatsApp profile updated successfully");
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to update WhatsApp profile");
    } finally {
      setSavingProfile(false);
    }
  };

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div className="page-wrapper">
      <div className="content">

        <div className="card">
          <div className="card-body pb-2">
            <h4 className="page-title mb-0">
              <span onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>
                <i className="ti ti-arrow-narrow-left me-3" />
              </span>
              WhatsApp Settings
            </h4>
          </div>
        </div>

        {wabaConfigured ? (
          loading ? (
            <div style={{ height: 400, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
            </div>
          ) : (
            <>
              <Card style={{ marginBottom: 24 }}>
                <Row gutter={24} align="middle">
                  <Col span={6}><strong>Display name</strong><div>{profileData?.profile?.displayName}</div></Col>
                  <Col span={6}><strong>Connected number</strong><div>{profileData?.phoneNumber}</div></Col>
                  <Col span={4}><strong>Message limits</strong><div>{profileData?.messagingLimit}</div></Col>
                  <Col span={4}>
                    <strong>Number status</strong><br />
                    <Tag color={profileData?.isConnected ? "green" : "red"}>
                      {profileData?.isConnected ? "CONNECTED" : "NOT CONNECTED"}
                    </Tag>
                  </Col>
                </Row>
                <Row gutter={24} style={{ marginTop: 16 }}>
                  <Col span={6}><strong>WhatsApp Business Account ID</strong><div>{profileData?.businessAccountId}</div></Col>
                  <Col span={6}>
                    <strong>Quality rating</strong><br />
                    {(() => {
                      const qr = profileData?.qualityRating;
                      const score = qr?.score || (typeof qr === "string" ? qr : "N/A");
                      return <Tag color={getQualityColor(score)}>{score}</Tag>;
                    })()}
                  </Col>
                  <Col span={6}>
                    <strong>Account status</strong><br />
                    <Tag color="blue">{profileData?.accountReviewStatus}</Tag>
                  </Col>
                </Row>
              </Card>

              <Card
                title="Business profile settings"
                extra={<Button type="primary" onClick={() => setTokenModalOpen(true)}>Update token</Button>}
                style={{ marginBottom: 24 }}
              >
                <Form form={form} layout="vertical">
                  <Form.Item label="WhatsApp profile picture">
                    <Row gutter={16} align="middle">
                      <Col>
                        <div style={{ width: 160, height: 160, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {imageUrl ? (
                            <img src={imageUrl} alt="preview" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 8 }} />
                          ) : (
                            <div style={{ textAlign: "center", color: "#999" }}>
                              <PictureOutlined style={{ fontSize: 32 }} />
                              <div style={{ marginTop: 8 }}>Upload image</div>
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col>
                        <div style={{ color: "#888", maxWidth: 220 }}>
                          Accepted formats: JPG/PNG<br />Minimum dimensions: 192×192 pixels<br />
                          <Upload showUploadList={false} beforeUpload={beforeUpload} onChange={handleImageChange}>
                            <div style={{ padding: 20, border: "2px dashed #d9d9d9", borderRadius: 8, cursor: "pointer", background: "#fafafa" }}>
                              Upload Image
                            </div>
                          </Upload>
                        </div>
                      </Col>
                    </Row>
                  </Form.Item>
                  <Form.Item label="Display Name (requires Meta approval)" name="displayName"><Input /></Form.Item>
                  <Form.Item label="Business address"     name="address"><Input /></Form.Item>
                  <Form.Item label="Business email"       name="email"><Input /></Form.Item>
                  <Form.Item label="Business description" name="description"><Input.TextArea /></Form.Item>
                  <Form.Item label="Business industry" name="vertical">
                    <Select placeholder="Select industry" allowClear>
                      <Select.Option value="AUTOMOTIVE">Automotive</Select.Option>
                      <Select.Option value="BEAUTY">Beauty, Spa &amp; Salon</Select.Option>
                      <Select.Option value="APPAREL">Clothing &amp; Apparel</Select.Option>
                      <Select.Option value="EDU">Education</Select.Option>
                      <Select.Option value="ENTERTAIN">Entertainment</Select.Option>
                      <Select.Option value="EVENT_PLAN">Event Planning &amp; Service</Select.Option>
                      <Select.Option value="FINANCE">Finance &amp; Banking</Select.Option>
                      <Select.Option value="GROCERY">Food &amp; Grocery</Select.Option>
                      <Select.Option value="GOVT">Government &amp; Politics</Select.Option>
                      <Select.Option value="HOTEL">Hotel &amp; Lodging</Select.Option>
                      <Select.Option value="HEALTH">Medical &amp; Health</Select.Option>
                      <Select.Option value="NONPROFIT">Non-profit</Select.Option>
                      <Select.Option value="PROF_SERVICES">Professional Services</Select.Option>
                      <Select.Option value="RETAIL">Retail</Select.Option>
                      <Select.Option value="TRAVEL">Travel &amp; Transportation</Select.Option>
                      <Select.Option value="RESTAURANT">Restaurant</Select.Option>
                      <Select.Option value="OTHER">Other</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item label="Website" name="websites"><Input placeholder="https://example.com" /></Form.Item>
                  <Button type="primary" onClick={handleSaveProfile} loading={savingProfile}>Save</Button>
                </Form>
              </Card>

              <Card title="Meta webhook settings" style={{ marginBottom: 24 }}>
                <Row gutter={16}>
                  <Col span={6}><strong>Webhook URL</strong></Col>
                  <Col span={18}>https://nf6fp9tcn6.execute-api.eu-north-1.amazonaws.com/api/whatsapp/webhook</Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 12 }}>
                  <Col span={6}><strong>Verify Token</strong></Col>
                  <Col span={18}>6933d0bee0693f675f1a9510</Col>
                </Row>
              </Card>

              <Card>
                <Row justify="space-between" align="middle">
                  <Col>
                    <strong>Remove WhatsApp account</strong>
                    <div style={{ color: "#888" }}>This will completely delete your WhatsApp integration.</div>
                  </Col>
                  <Col>
                    <Button danger type="primary" onClick={handleDeleteIntegration}>Delete integration</Button>
                  </Col>
                </Row>
              </Card>
            </>
          )
        ) : (

          /* ── Not connected ─────────────────────────────────────────────── */
          <div style={{ height: "70vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
            <div style={{
              background: "#fff", borderRadius: 16, padding: "48px 56px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.08)", maxWidth: 480, width: "100%", textAlign: "center",
            }}>
              {/* WhatsApp logo */}
              <div style={{
                width: 72, height: 72, background: "#25D366", borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px",
              }}>
                <svg viewBox="0 0 24 24" fill="white" width={40} height={40}>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.886 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>

              <h3 style={{ marginBottom: 8 }}>Connect WhatsApp Business</h3>
              <p style={{ color: "#888", marginBottom: 32, lineHeight: 1.6 }}>
                Connect your WhatsApp Business Account to start sending and receiving messages directly in the CRM.
              </p>

              {/* Primary: Continue with Meta */}
              <button
                onClick={handleEmbeddedSignup}
                disabled={connecting}
                style={{
                  width: "100%", padding: "13px 24px", borderRadius: 8, border: "none",
                  background: connecting ? "#5b9ef5" : "#1877F2", color: "#fff",
                  fontSize: 16, fontWeight: 600, cursor: connecting ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  transition: "background 0.2s",
                }}
              >
                {connecting ? (
                  <><Spin indicator={<LoadingOutlined style={{ fontSize: 18, color: "#fff" }} spin />} /> Connecting…</>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="white" width={22} height={22}>
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    Continue with Meta
                  </>
                )}
              </button>

              {/* Secondary: manual fallback */}
              {/* <div style={{ marginTop: 20, color: "#bbb", fontSize: 13 }}>
                Having trouble?{" "}
                <span
                  style={{ color: "#1677ff", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => setConfigModalOpen(true)}
                >
                  Enter credentials manually
                </span>
              </div> */}
            </div>
          </div>
        )}

        {/* Token modal */}
        <Modal title="Update Access Token" open={tokenModalOpen} onCancel={() => setTokenModalOpen(false)}
          onOk={handleRegenerateToken} okText="Update" confirmLoading={regenerating}>
          <Form form={tokenForm} layout="vertical">
            <Form.Item name="accessToken"><Input.Password /></Form.Item>
          </Form>
        </Modal>

        {/* Manual config modal */}
        <Modal title="WhatsApp API config" open={configModalOpen} onCancel={() => setConfigModalOpen(false)}
          onOk={() => handleSaveConfig(false)} okText="Save" confirmLoading={connecting}>
          <Form form={configForm} layout="vertical">
            <Form.Item label="App ID"             name="appId"         rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item label="Access Token"       name="accessToken"   rules={[{ required: true }]}><Input.Password /></Form.Item>
            <Form.Item label="Phone Number ID"    name="phoneNumberId" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item label="Business Account ID" name="wabaId"       rules={[{ required: true }]}><Input /></Form.Item>
          </Form>
        </Modal>

        {/* Force-disconnect confirmation */}
        <Modal title="WABA Already Connected" open={forceConfirmVisible}
          onCancel={() => { setForceConfirmVisible(false); setPendingPayload(null); setConnecting(false); }}
          onOk={handleForceConnect} okText="Yes, Disconnect & Connect"
          okButtonProps={{ danger: true, loading: connecting }} cancelText="Cancel">
          <p>This WABA is currently connected to <strong>{forceConfirmEmail}</strong>.</p>
          <p>Proceeding will disconnect it from that account. Would you like to continue?</p>
        </Modal>

      </div>
    </div>
  );
};

export default WabaSetting;
